import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { StreamingOverviewResponse } from 'fym-shared/src/music24/streaming';
import { TypedMemo } from '../../../common/types';
import { Segmented } from '../../components/primitives/Segmented';
import { useStreamingDimension } from '../hooks/useStreamingDimension';
import { buildDimensionRows, DimensionMetric } from '../dimensionRows';
import { streamingDimensionPath, streamingSectionPath } from '../streamingPaths';
import { sourceNames } from '../streamingSources';
import { streamingTotalLabels } from '../streamingTotalsMessages';
import { streamingBlockMessages as messages } from '../streamingBlockMessages';
import { StreamingBlock } from '../parts/StreamingBlock';
import { RankedRow } from '../parts/RankedRow';
import { DimensionName } from '../parts/DimensionName';
import { DimensionDetail } from '../parts/DimensionDetail';
import { DimensionNote } from '../parts/DimensionNote';
import { StreamingPlanLockCard } from '../parts/StreamingPlanLockCard';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { streamingEarningsMessages } from '../streamingEarningsMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface DimensionSectionProps {
  accountId: number;
  overview: StreamingOverviewResponse;
  dimension: 'store' | 'country';
  onNavigate: (path: string) => void;
}

/**
 * Streaming → Stores or Countries (design c1, c2): the ranked list and the detail of `?store=` /
 * `?country=`. On a plan without the split (Free) the plan card.
 */
const DimensionSectionComponent: React.FC<DimensionSectionProps> = ({ accountId, overview, dimension, onNavigate }) => {
  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const [metric, setMetric] = useState<DimensionMetric>('streams');
  const { data, isLoading, error, retry } = useStreamingDimension({
    accountId,
    dimension,
    days: overview.days,
    skip: false,
  });
  const rows = useMemo(() => buildDimensionRows(data?.totals ?? [], metric), [data, metric]);
  const picked = searchParams.get(dimension);
  const pickedRow = useMemo(() => rows.find((row) => row.value === picked), [picked, rows]);
  const pickedTotal = useMemo(() => data?.totals.find((total) => total.value === picked), [data, picked]);
  const isStore = dimension === 'store';
  const section = isStore ? 'stores' : 'countries';

  const metricOptions = useMemo(
    () => [
      { value: 'streams' as const, label: intl.formatMessage(streamingTotalLabels.streams) },
      { value: 'downloads' as const, label: intl.formatMessage(streamingTotalLabels.downloads) },
    ],
    [intl]
  );

  // The plan does not split the streams (Free): the plan card in place of the list.
  if (data?.splitHidden === true || overview.sections?.[section]?.locked === 'plan') {
    return (
      <StreamingPlanLockCard
        feature={SMLUpgradeFeature.streamingSplit}
        title={streamingEarningsMessages.splitPlanTitle}
        testId={`streaming-${section}-plan-lock`}
      />
    );
  }

  return (
    <div className={styles.blocksTwo}>
      <StreamingBlock
        title={intl.formatMessage(isStore ? messages.stores : messages.countries)}
        meta={intl.formatMessage(isStore ? messages.storesCount : messages.countriesCount, { count: rows.length })}
        isLoading={isLoading}
        error={error}
        retry={retry}
        testId={`streaming-section-${section}`}
      >
        <Segmented options={metricOptions} value={metric} onChange={setMetric} />
        {rows.length === 0 ? (
          <p className={styles.faint}>{intl.formatMessage(messages.emptyList)}</p>
        ) : (
          <ol className={styles.rows}>
            {rows.map((row) => (
              <RankedRow
                key={row.value}
                rank={row.rank}
                name={<DimensionName dimension={dimension} value={row.value} />}
                share={row.share}
                value={row.number}
                changePct={row.changePct}
                isActive={row.value === picked}
                onOpen={() => onNavigate(streamingDimensionPath(dimension, row.value, overview.days))}
              />
            ))}
          </ol>
        )}
        {isStore && rows.length > 0 ? (
          <p className={styles.footnote} data-testid="streaming-stores-sum-note">
            {intl.formatMessage(messages.storesSumNote)}
          </p>
        ) : null}
        {data !== undefined && (data.sources ?? []).length > 0 ? (
          <DimensionNote source={sourceNames(data.sources ?? [], 'shortName')} answer={data} />
        ) : null}
      </StreamingBlock>
      {pickedRow !== undefined && pickedTotal !== undefined ? (
        <DimensionDetail
          dimension={dimension}
          row={pickedRow}
          total={pickedTotal}
          count={rows.length}
          series={data?.series.find((one) => one.value === picked)}
          onClose={() => onNavigate(streamingSectionPath(section, overview.days))}
        />
      ) : null}
    </div>
  );
};

export const DimensionSection = TypedMemo(DimensionSectionComponent);
